import { PatientAdvance } from '../interfaces/payment';

export type EstadoCobranca =
  | 'ocioso' | 'enviando' | 'aguardando' | 'confirmado' | 'sem-confirmacao' | 'erro';

export type EventoCobranca =
  | 'enviar' | 'enviado' | 'sucesso' | 'falha' | 'timeout'
  | 'conferencia-achou' | 'conferencia-nao-achou' | 'reset';

export const TIMEOUT_TERMINAL_MS = 90000;

// 180 é o teto que o back aceita (Math.Min no GetPatientAdvances); pedir mais é silenciosamente
// reduzido lá.
export const DIAS_HISTORICO_ADIANTAMENTOS = 180;

// Média medida em produção: 2,1 adiantamentos por paciente em 180 dias, máximo 38. A lista
// cabe inteira na resposta — a paginação é só de exibição, não vai ao servidor.
export const TAMANHO_PAGINA_ADIANTAMENTOS = 5;

// Estorno so vale em adiantamento intacto: com parte do saldo aplicada a uma conta, o estorno
// devolve o recibo inteiro. Medido em producao: 3.433 dos 62.131 adiantamentos de 30 dias tem
// saldo positivo mas menor que o valor pago.
export function saldoIntacto(vlSaldo: number, vlAdiantamento: number): boolean {
  return vlSaldo > 0 && vlSaldo === vlAdiantamento;
}

export function totalPaginas(itens: number, tamanho = TAMANHO_PAGINA_ADIANTAMENTOS): number {
  return Math.max(1, Math.ceil(itens / tamanho));
}

// Lista vazia e página fora do intervalo caem na 1: a lista encolhe entre carregamentos
// (estorno, saldo consumido por outro operador) e a página guardada deixa de existir.
export function paginaValida(pagina: number, itens: number, tamanho = TAMANHO_PAGINA_ADIANTAMENTOS): number {
  return Math.min(Math.max(1, pagina), totalPaginas(itens, tamanho));
}

// O hub confirma quando o terminal aprova, mas o adiantamento só é gravado depois, pelo
// serviço de pagamentos. As esperas crescentes cobrem essa janela sem travar a tela.
export const ESPERAS_BUSCA_ADIANTAMENTO_MS = [2000, 3000, 5000, 8000, 12000];

export function proximoEstado(atual: EstadoCobranca, evento: EventoCobranca): EstadoCobranca {
  if (evento === 'reset') return 'ocioso';
  // 'enviar' escapa da trava de 'confirmado' porque só existe por clique do operador.
  if (evento === 'enviar') return 'enviando';
  if (atual === 'confirmado') return 'confirmado';

  switch (evento) {
    case 'enviado': return 'aguardando';
    case 'sucesso': return 'confirmado';
    case 'conferencia-achou': return 'confirmado';
    case 'falha': return 'erro';
    // Silêncio do terminal não é aprovação. 'enviando' entra aqui pelo 504: pode ter cobrado.
    case 'timeout': return atual === 'aguardando' || atual === 'enviando' ? 'sem-confirmacao' : atual;
    case 'conferencia-nao-achou': return atual;
  }
  return atual;
}

// Estorno de espécie só vale no dia do recebimento (o back recusa depois disso).
export function ehDeHoje(dataIso: string | null | undefined, hoje = new Date()): boolean {
  if (!dataIso) return false;
  const d = new Date(dataIso);
  if (isNaN(d.getTime())) return false;
  return d.getFullYear() === hoje.getFullYear()
    && d.getMonth() === hoje.getMonth()
    && d.getDate() === hoje.getDate();
}

export function extrairDocumentoDoReference(referenceId: string): string | null {
  const partes = (referenceId ?? '').split('-');
  if (partes.length < 4 || partes[0] !== 'PAYMENT' || !partes[1]) return null;
  return partes[1];
}

// Duas marcas diferentes no banco: espécie grava IE_STATUS = 'P'; Adyen só zera o saldo e
// escreve o motivo na observação. vlSaldo === 0 não serve como critério — também acontece no
// uso normal. Daí a observação ser heurística: o texto exato não é contrato.
export function adiantamentoEstornado(a: Pick<PatientAdvance, 'ieStatus' | 'dsObservacao'>): boolean {
  if (a.ieStatus === 'P') return true;
  const obs = (a.dsObservacao ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
  return obs.includes('estorno');
}
